// ENG-20861 inline `isString` rather than importing from vue-composable — this module is
// imported cross-package by the backend vue-unit boot, which can't resolve frontend deps.
const isString = (val: unknown): val is string => typeof val === "string";

export const INVALID_FORMAT_MESSAGE = "[INVALID DATE FORMAT]";

function invalidDateFormat(e: any) {
  if (!e) return;
  try {
    // just to keep the call stack
    throw new Error(`${e} has invalid format`);
  } catch (e) {
    console.error(e);
    window.captureException?.(e);
    return INVALID_FORMAT_MESSAGE;
  }
}

// parsing date based on Medicus requirements
// MED-9661
export function parseDate(e: string | Date | number | any) {
  if (!e) return "";
  if (e instanceof Date) return e;
  if (typeof e === "number") {
    return new Date(e);
  }
  if (!isString(e)) return invalidDateFormat(e);

  const [dateStr, timeStr] = e.split(" ");
  const [year, month, day] = dateStr.split("-").map((x) => +x);

  // let d = new Date(year, month - 1, day);
  let d = new Date(year, month - 1, day);
  if (timeStr) {
    const [hours, minutes, seconds] = timeStr.split(":").map((x) => +x);
    // hour validation
    if (hours > 23 || hours < 0 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59)
      return invalidDateFormat(e);

    d = adjustDate(d, {
      hours,
      minutes: minutes,
      seconds: seconds ?? 0,
    });

    // // remove possible timezone info
    // d = addToDate(d, { minutes: d.getTimezoneOffset() });
  }

  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return invalidDateFormat(e);
  }

  return d;
}

export function formatDateTime(e: any, weekday?: boolean) {
  if (!e) return "";
  const d = parseDate(e);
  if (d === INVALID_FORMAT_MESSAGE) return d;
  // TODO this should be driven by organisation locale or user?
  return formatDatePattern(d, `${weekday ? "ddd " : ""}DD MMM YYYY, HH:mm`) || "";
}

export function formatDate(e: any, weekday?: boolean) {
  return formatDateTime(e, weekday).split(",")[0];
}

export function formatDateTimeMedicus(e: any) {
  if (!e) return "";
  const d = parseDate(e);
  if (d === INVALID_FORMAT_MESSAGE) return d;
  return formatDatePattern(d, "YYYY-MM-DD HH:mm:ss") || "";
}

export function getAge(e: any, t = new Date(), append?: boolean) {
  if (!e) return "";
  const d = parseDate(e);
  if (d === INVALID_FORMAT_MESSAGE) return d;
  const months = getDateDiff(t, d, "months");
  const r = months >= 12 ? Math.floor(months / 12) : months;

  if (append) {
    return `${r}${months >= 12 ? "y" : "m"}`;
  }
  return r;
}

export function getMonths(e: any, t = new Date()) {
  if (!e) return "";
  const d = parseDate(e);
  if (d === INVALID_FORMAT_MESSAGE) return d;
  return Math.floor(getDateDiff(t, d, "months"));
}

export function getMinutes(e: any, t = new Date()) {
  if (!e) return "";
  const d = parseDate(e);
  if (d === INVALID_FORMAT_MESSAGE) return d;
  return Math.floor(getDateDiff(t, d, "minutes"));
}

export function isValidTime(time) {
  if (!time) return;
  return /^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]/.test(time);
}

export function addOrdinalSuffix(n) {
  const j = n % 10,
    k = n % 100;

  if (j === 1 && k !== 11) return n + "st";
  if (j === 2 && k !== 12) return n + "nd";
  if (j === 3 && k !== 13) return n + "rd";
  return n + "th";
}

// ENG-20855: native (Quasar-free) replacements for the quasar `date.*` helpers used by the
// date-picker components (MDate, MDateTime, MDatePicker). Kept here so the components can drop
// `import { date } from "quasar"`.

export interface DateDuration {
  years?: number;
  months?: number;
  weeks?: number;
  days?: number;
  hours?: number;
  minutes?: number;
}

// Returns a new Date with the duration added (does not mutate the input). Month/year overflow
// follows native Date semantics (e.g. 31 Jan + 1 month rolls into March).
export function addToDate(d: Date, duration: DateDuration): Date {
  const r = new Date(d.getTime());
  if (duration.years) r.setFullYear(r.getFullYear() + duration.years);
  if (duration.months) r.setMonth(r.getMonth() + duration.months);
  if (duration.weeks) r.setDate(r.getDate() + duration.weeks * 7);
  if (duration.days) r.setDate(r.getDate() + duration.days);
  if (duration.hours) r.setHours(r.getHours() + duration.hours);
  if (duration.minutes) r.setMinutes(r.getMinutes() + duration.minutes);
  return r;
}

export function subtractFromDate(d: Date, duration: DateDuration): Date {
  const negated: DateDuration = {};
  (Object.keys(duration) as (keyof DateDuration)[]).forEach((k) => {
    const v = duration[k];
    if (v != null) negated[k] = -v;
  });
  return addToDate(d, negated);
}

// Replacement for quasar `date.adjustDate` as used here: sets (not adds) hours/minutes/seconds
// to absolute values on a clone of `d`, leaving year/month/day untouched.
export function adjustDate(
  d: Date,
  parts: { hours?: number; minutes?: number; seconds?: number },
): Date {
  const r = new Date(d.getTime());
  if (parts.hours !== undefined) r.setHours(parts.hours);
  if (parts.minutes !== undefined) r.setMinutes(parts.minutes);
  if (parts.seconds !== undefined) r.setSeconds(parts.seconds);
  return r;
}

const MILLISECONDS_IN_DAY = 86400000;
const MILLISECONDS_IN_HOUR = 3600000;
const MILLISECONDS_IN_MINUTE = 60000;

export type DateDiffUnit =
  | "years"
  | "year"
  | "months"
  | "month"
  | "days"
  | "day"
  | "date"
  | "hours"
  | "hour"
  | "minutes"
  | "minute"
  | "seconds"
  | "second";

// Zeroes the components below `unit` (mirrors quasar's startOfDate cascade). Only the
// units getDateDiff actually needs — years/months are computed arithmetically, not via this.
function startOfDate(
  d: Date,
  unit: "day" | "days" | "date" | "hour" | "hours" | "minute" | "minutes" | "second" | "seconds",
): Date {
  const t = new Date(d.getTime());
  switch (unit) {
    case "day":
    case "days":
    case "date":
      t.setHours(0);
    // falls through
    case "hour":
    case "hours":
      t.setMinutes(0);
    // falls through
    case "minute":
    case "minutes":
      t.setSeconds(0);
    // falls through
    case "second":
    case "seconds":
      t.setMilliseconds(0);
  }
  return t;
}

// Timezone-aware diff (mirrors quasar's getDiff).
function getDiff(t: Date, sub: Date, interval: number): number {
  return (
    (t.getTime() -
      t.getTimezoneOffset() * MILLISECONDS_IN_MINUTE -
      (sub.getTime() - sub.getTimezoneOffset() * MILLISECONDS_IN_MINUTE)) /
    interval
  );
}

// Replacement for quasar `date.getDateDiff`. Matches quasar's semantics for every unit.
export function getDateDiff(date: Date, subtract: Date, unit: DateDiffUnit = "days"): number {
  const t = new Date(date);
  const sub = new Date(subtract);

  switch (unit) {
    case "years":
    case "year":
      return t.getFullYear() - sub.getFullYear();
    case "months":
    case "month":
      return (t.getFullYear() - sub.getFullYear()) * 12 + t.getMonth() - sub.getMonth();
    case "days":
    case "day":
    case "date":
      return getDiff(startOfDate(t, "day"), startOfDate(sub, "day"), MILLISECONDS_IN_DAY);
    case "hours":
    case "hour":
      return getDiff(startOfDate(t, "hour"), startOfDate(sub, "hour"), MILLISECONDS_IN_HOUR);
    case "minutes":
    case "minute":
      return getDiff(startOfDate(t, "minute"), startOfDate(sub, "minute"), MILLISECONDS_IN_MINUTE);
    case "seconds":
    case "second":
      return getDiff(startOfDate(t, "second"), startOfDate(sub, "second"), 1000);
  }
}

// Replacements for quasar `date.getMinDate` / `date.getMaxDate`. Return a timestamp number after
// the first comparison (matches quasar — callers wrap in `new Date(...)`).
export function getMinDate(date: Date | string | number, ...args: Array<Date | string | number>) {
  let t: number | Date = new Date(date);
  args.forEach((d) => {
    t = Math.min(t as number, new Date(d) as unknown as number);
  });
  return t;
}

export function getMaxDate(date: Date | string | number, ...args: Array<Date | string | number>) {
  let t: number | Date = new Date(date);
  args.forEach((d) => {
    t = Math.max(t as number, new Date(d) as unknown as number);
  });
  return t;
}

// Replacement for quasar `date.clone`.
export function clone<T>(date: T): T {
  return date instanceof Date ? (new Date(date.getTime()) as unknown as T) : date;
}

export interface WithinRangeOptions {
  inclusiveFrom?: boolean;
  inclusiveTo?: boolean;
  // compare calendar dates only, ignoring the time component
  onlyDate?: boolean;
}

function stripTime(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Replacement for quasar `date.isBetweenDates`. Strict (exclusive) bounds by default.
export function isWithinRange(
  d: Date,
  min: Date,
  max: Date,
  options: WithinRangeOptions = {},
): boolean {
  const { inclusiveFrom = false, inclusiveTo = false, onlyDate = false } = options;
  const t = (onlyDate ? stripTime(d) : d).getTime();
  const lo = (onlyDate ? stripTime(min) : min).getTime();
  const hi = (onlyDate ? stripTime(max) : max).getTime();
  const afterMin = inclusiveFrom ? t >= lo : t > lo;
  const beforeMax = inclusiveTo ? t <= hi : t < hi;
  return afterMin && beforeMax;
}

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const SHORT_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Minimal token formatter covering the masks used across the app: YYYY, MMM, MM, DD, ddd, HH, mm, ss.
// Single-pass replace (original string only) so substituted text is never re-matched.
export function formatDatePattern(val: Date | string | number, pattern: string): string {
  const d = val instanceof Date ? val : new Date(val);
  if (isNaN(d.getTime())) return "";

  const pad = (n: number) => n.toString().padStart(2, "0");
  const tokens: Record<string, string> = {
    YYYY: d.getFullYear().toString(),
    MMM: SHORT_MONTHS[d.getMonth()],
    MM: pad(d.getMonth() + 1),
    DD: pad(d.getDate()),
    ddd: SHORT_DAYS[d.getDay()],
    HH: pad(d.getHours()),
    mm: pad(d.getMinutes()),
    ss: pad(d.getSeconds()),
  };
  return pattern.replace(/YYYY|MMM|MM|DD|ddd|HH|mm|ss/g, (m) => tokens[m]);
}

// ENG-20861: in-house replacement for the quasar `date` namespace exposed globally as `$date`.
// Only the methods actually consumed via `$date.*` across the app are included.
export const dateNamespace = {
  formatDate: formatDatePattern,
  addToDate,
  subtractFromDate,
  adjustDate,
  getDateDiff,
  getMinDate,
  getMaxDate,
  isBetweenDates: isWithinRange,
  clone,
};
